// 🔴 [중요] 사원 판단 = customer.tags 의 `employee` 태그 (SSOT, 서버측 데이터, 위변조 불가).
//          본 팝업의 mode 선택 → sessionStorage 저장 (FE 표시 토글) +
//          cart.attribute("employee_payment_mode") / cart.attribute("is_employee_sale") 마킹 (BE 신호).
//          BE Function (misto-msa: employee-purchase-discount) 은 AND 조건으로 할인 적용:
//            customer.tags includes "employee" AND employee-sale cart attribute enabled
//          → tags 가 SSOT 라서 attribute 위변조해도 비사원 계정엔 할인 미적용 (보안 OK).
//          → mode === "normal" 선택 시 attribute false/blank → AND false → 자동 할인 해제됨.
//          박제: docs/be-handoff/2026-05-12-employee-payment-popup.md, wiki/docs/employee-sale-architecture.md

class EmployeePaymentPopup extends ModalDialog {
  static STORAGE_KEY = "employee_payment_mode";

  constructor() {
    super();
    this.attachExtraListeners();
  }

  attachExtraListeners() {
    this.querySelectorAll(".js-button-close").forEach((btn) => {
      btn.addEventListener("click", () => this.hide());
    });

    this.radios = this.querySelectorAll(".employee-payment-popup__radio");
    this.applyBtn = this.querySelector(".employee-payment-popup__apply");

    this.radios.forEach((radio) => {
      radio.addEventListener("change", () => this.handleRadioChange());
    });

    if (this.applyBtn) {
      this.applyBtn.addEventListener("click", () => this.handleApply());
    }
  }

  async handleRadioChange() {
    const mode = this.getSelectedMode();
    if (this.applyBtn) this.applyBtn.disabled = !mode;
  }

  // 가격 표시 모드 토글 — employee 선택 시 html.employee-discount-applied 클래스 추가, 아니면 제거.
  // default = 정가 표시. employee 선택 → 50% 할인가로 swap.
  // FOUC 방지를 위해 head inline script (layout/theme.liquid) 가 paint 전에도 동일 작업 수행.
  static applyDisplayMode(mode) {
    if (mode === "employee") {
      document.documentElement.classList.add("employee-discount-applied");
    } else {
      document.documentElement.classList.remove("employee-discount-applied");
    }
  }

  async handleApply() {
    const mode = this.getSelectedMode();
    if (!mode) return;
    window.MISTO_DIAG && window.MISTO_DIAG.emit("popup_apply_clicked", { selected_mode: mode });
    if (this.applyBtn) this.applyBtn.disabled = true;
    const ok = await this.applyMode(mode);
    if (!ok && this.applyBtn) this.applyBtn.disabled = false;
  }

  getSelectedMode() {
    const selected = Array.from(this.radios).find((r) => r.checked);
    const mode = selected?.value;
    return mode === "employee" || mode === "normal" ? mode : null;
  }

  async applyMode(mode) {
    // 🔴 사원 모드 선택 시 한도조회 사전 검증 — BE Function 한도검증과 중복이지만 UX 측 가드.
    //    fail 응답 시: sessionStorage 미선택 (removeItem) + fail_pending flag + cart attribute 클리어 + reload
    //    → reload 후 fail 모달만 노출 (popup 은 fail_pending 동안 connectedCallback 에서 차단)
    //    cart total > remaining 비교는 여기서 안 함 (/cart 진입 / checkout 시점 가드).
    if (mode === "employee" && window.EmployeeLimitCheck) {
      const result = await window.EmployeeLimitCheck.fetchRemaining();
      if (!result.success) {
        try {
          sessionStorage.removeItem(EmployeePaymentPopup.STORAGE_KEY);
          sessionStorage.setItem("employee_limit_fail_pending", "1");
          // 🔴 진단 — reload 넘김용 원인 컨텍스트 (fail_modal_shown 이 읽음).
          if (window.EmployeeLimitCheck && typeof window.EmployeeLimitCheck.saveFailContext === "function") {
            window.EmployeeLimitCheck.saveFailContext(result, "popup");
          } else {
            sessionStorage.setItem("diag_fail_ctx", JSON.stringify({ reason: result.reason || "limit_check_fail", caller: "popup" }));
          }
        } catch (e) {}
        window.MISTO_DIAG && window.MISTO_DIAG.emit("employee_mode_cleared", { reason: "fail", prev_mode: "employee", caller: "popup" });
        EmployeePaymentPopup.applyDisplayMode(null);
        await this.markCartAttribute("normal");
        window.location.reload();
        return false;
      }
    }

    // 🔴 BE 신호 마킹 먼저 — 실패 시 sessionStorage / 표시 클래스도 안 바꿈.
    //    "FE 는 할인가 표시 / BE 는 정가 결제" 괴리 발생 방지.
    const ok = await this.markCartAttribute(mode);
    if (!ok) {
      console.error("[employee-payment] cart attribute 마킹 실패 — 선택 재시도 필요");
      return false;
    }

    // 🔴 사원판매 모드 ON 전환 시 기존 promo·쿠폰·BXGY GY 일괄 cleanup (2026-05-27).
    //   사원가(50%) + promo discount 중복 적용 차단. 정책 SSOT: wiki/docs/cart-promo-cleanup-policy.md
    //   OFF (normal) 전환 시엔 cleanup 안 함 — 일반 사용자 모드로 돌아갈 뿐, 다시 promo 적용 가능.
    if (mode === "employee" && window.CartPromoCleanup && typeof window.CartPromoCleanup.run === "function") {
      try {
        await window.CartPromoCleanup.run({ source: "employee-mode-on" });
      } catch (err) {
        console.warn("[employee-payment] cleanup 실패 (계속 진행)", err);
      }
    }

    sessionStorage.setItem(EmployeePaymentPopup.STORAGE_KEY, mode);
    EmployeePaymentPopup.applyDisplayMode(mode);
    window.location.reload();
    return true;
  }

  // BE 신호 — cart attribute 마킹. Shopify Function `employee-purchase-discount` 의 AND 조건 입력.
  //   "employee" 선택 → employee_payment_mode="employee", is_employee_sale="true"
  //   "normal" 선택 → employee_payment_mode="", is_employee_sale="false"
  //   Function 측 검증: customer.tags (employee) AND employee-sale cart attribute enabled
  async markCartAttribute(mode) {
    const targetMode = mode === "employee" ? "employee" : "";
    window.MISTO_DIAG && window.MISTO_DIAG.emit("cart_attribute_update_start", { target_mode: targetMode, caller: "popup" });
    try {
      const res = await fetch(MISTO.localeUrl('/cart/update.js'), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          attributes: {
            employee_payment_mode: targetMode,
            is_employee_sale: mode === "employee" ? "true" : "false",
          },
        }),
      });
      window.MISTO_DIAG && window.MISTO_DIAG.emit("cart_attribute_update_result", { target_mode: targetMode, ok: res.ok, http_status: res.status, caller: "popup" });
      return res.ok;
    } catch (e) {
      console.warn("[employee-payment] cart attribute fetch 실패:", e);
      window.MISTO_DIAG && window.MISTO_DIAG.emit("cart_attribute_update_result", { target_mode: targetMode, ok: false, reason: "fetch_exception", error_code: e && e.name, caller: "popup" });
      return false;
    }
  }

  connectedCallback() {
    // Theme editor 미리보기 — section.settings.show_popup_on_customizer + design mode
    if (Shopify.designMode) {
      if (this.dataset.openInDesignMode === "true") {
        super.connectedCallback();
        this.show();
      }
      return;
    }

    // 🔴 한도 fail 직후 reload 진입 시 fail 모달만 노출되도록 popup 차단.
    //    fail_pending flag 는 EmployeeLimitCheck.init 이 fail 모달 노출하면서 clear.
    try {
      if (sessionStorage.getItem("employee_limit_fail_pending") === "1") return;
    } catch (e) {}

    const loggedIn = this.dataset.customerLoggedIn === "true";
    const isEmployee = this.dataset.isEmployee === "true";

    // 로그아웃 또는 비사원 → sessionStorage 정리 + 가격 모드 클래스 제거 후 종료
    // (재로그인 시 다시 선택 받기 위해)
    if (!loggedIn || !isEmployee) {
      sessionStorage.removeItem(EmployeePaymentPopup.STORAGE_KEY);
      EmployeePaymentPopup.applyDisplayMode(null);
      this.markCartAttribute("normal");
      return;
    }

    super.connectedCallback();

    // 이미 선택값 있으면 노출 X (단, 유효한 값일 때만)
    const stored = sessionStorage.getItem(EmployeePaymentPopup.STORAGE_KEY);
    if (stored === "employee" || stored === "normal") {
      window.MISTO_DIAG && window.MISTO_DIAG.emit("employee_mode_restored", { stored_mode: stored });
      this.markAndRefresh(stored);
      return;
    }
    if (stored !== null) {
      // 알 수 없는 값 → 영구 차단 방지 위해 정리
      sessionStorage.removeItem(EmployeePaymentPopup.STORAGE_KEY);
      EmployeePaymentPopup.applyDisplayMode(null);
    }

    // 🔴 (2026-05-28) 사원 + 미선택 상태 = cart attribute 'normal' 강제 마킹.
    //   회귀: 미선택인데 cart 의 is_employee_sale 가 stale 'true' 박혀있어 drawer 가 employee 가격 자동 표시.
    //   popup 띄우는 동안 일반 가격으로 강제 → 사용자가 mode 선택하면 그에 맞춰 다시 마킹.
    this.markAndRefresh("normal");

    // 사원 + 미선택 → 즉시 노출
    window.MISTO_DIAG && window.MISTO_DIAG.emit("popup_shown", {});
    this.show();
  }

  // 🔴 (2026-05-28 D11 fix) cart attribute 마킹 + drawer SSR refresh.
  //   drawer SSR HTML 이 이전 mode 시점에 받혀서 stale Employee 라벨/노드 잔재 가능 →
  //   markCartAttribute 완료 후 CartPromoCleanup.refreshDrawerSections 호출로 fresh SSR 재요청.
  //   회귀 case: 사원 mode='employee' 시점 SSR 받은 drawer → 미선택 진입 시 D10 fix 가 cart attr
  //              'false' 마킹하지만 drawer DOM 은 stale Employee 라벨/data-employee-* 노드 유지.
  //   박제: docs/test-scenarios/bxgy-y-only-modal-2026-05-28.html D11
  markAndRefresh(mode) {
    this.markCartAttribute(mode)
      .then((ok) => {
        if (ok && window.CartPromoCleanup && typeof window.CartPromoCleanup.refreshDrawerSections === "function") {
          window.CartPromoCleanup.refreshDrawerSections();
        }
      })
      .catch((err) => {
        console.warn("[employee-payment] markAndRefresh failed:", err);
      });
  }
}

customElements.define("employee-payment-popup", EmployeePaymentPopup);

// ── 위시리스트 (Swym) 사원 가격 처리 ──────────────────────────
// Swym 은 외부 라이브러리. 가격을 client-side 렌더링 → SSR 안 거침.
// MutationObserver 로 위시리스트 item 출현 감지 → 사원이면 정가/할인가 두 노드 박음.
// CSS swap (기존 [data-employee-original]/[data-employee-discounted] rule) 으로 mode 토글.
//
// 🔴 [2026-05-29] Swym DOM 에 compare_at 노출됨 (`.swym-storefront-layout-grid-item-compare-price`).
//    → 사원가 = min(현재가, compare_at × 0.5) 로 price.liquid / BE Function 과 동일하게 미러.
//    취소선 = compare_at(기준값). 원본 compare 노드는 employee 모드에서 [data-employee-original] 로 숨김.
//    (이전: compare 접근 불가로 price/2 근사 → 깨짐. 회귀 박제: wiki/docs/employee-sale-architecture.md)

(function () {
  const PRICE_SELECTOR = ".swym-storefront-layout-grid-item-final-price";
  const ITEM_SELECTOR = ".swym-storefront-layout-grid-item";

  function moneyFormat(amount) {
    return (
      "HK$ " +
      amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  function isEmployeeCustomer() {
    const popup = document.querySelector("employee-payment-popup");
    return popup?.dataset.isEmployee === "true";
  }

  function processSwymWishlistItems() {
    if (!isEmployeeCustomer()) return;
    const items = document.querySelectorAll(ITEM_SELECTOR);
    items.forEach((item) => {
      if (item.dataset.employeePriceProcessed === "1") return;
      const priceNode = item.querySelector(PRICE_SELECTOR);
      if (!priceNode) return;
      if (
        priceNode.hasAttribute("data-employee-original") ||
        priceNode.hasAttribute("data-employee-discounted")
      )
        return;

      const curMatch = priceNode.textContent.trim().match(/[\d,]+\.\d+/);
      if (!curMatch) return;
      const current = parseFloat(curMatch[0].replace(/,/g, ""));
      if (isNaN(current) || current <= 0) return;

      // 🔴 [2026-05-29] 정가(compare_at) = Swym compare-price 노드 (있으면). 없으면 현재가.
      //   사원가 = min(현재가, 정가 × 0.5) — price.liquid / BE Function 과 동일 정책.
      //   취소선 = 정가(기준값). 원본 compare 노드는 employee 모드에서 숨김(중복 방지).
      const compareNode = item.querySelector(
        ".swym-storefront-layout-grid-item-compare-price",
      );
      let reference = current;
      if (compareNode) {
        const cmpMatch = compareNode.textContent.trim().match(/[\d,]+\.\d+/);
        if (cmpMatch) {
          const cmp = parseFloat(cmpMatch[0].replace(/,/g, ""));
          if (!isNaN(cmp) && cmp > 0) reference = cmp;
        }
        compareNode.setAttribute("data-employee-original", "");
      }
      const discounted = Math.min(current, reference * 0.5);

      const originalSpan = document.createElement("span");
      originalSpan.className = priceNode.className;
      originalSpan.setAttribute("data-employee-original", "");
      originalSpan.textContent = moneyFormat(current);

      const discountedSpan = document.createElement("span");
      discountedSpan.className = priceNode.className;
      discountedSpan.setAttribute("data-employee-discounted", "");
      discountedSpan.innerHTML =
        '<s>' +
        moneyFormat(reference) +
        '</s> <ins class="color-red">' +
        moneyFormat(discounted) +
        "</ins>";

      priceNode.parentNode.insertBefore(originalSpan, priceNode);
      priceNode.parentNode.insertBefore(discountedSpan, priceNode);
      priceNode.remove();

      item.dataset.employeePriceProcessed = "1";
    });
  }

  function initSwymWishlistObserver() {
    if (!isEmployeeCustomer()) return;
    processSwymWishlistItems();
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (
            node.matches?.(ITEM_SELECTOR) ||
            node.querySelector?.(ITEM_SELECTOR)
          ) {
            processSwymWishlistItems();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initSwymWishlistObserver);
  } else {
    initSwymWishlistObserver();
  }
})();
